import React, { FC, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Box, Grid, Typography, IconButton, SvgIcon } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { GetUserProfileResponseDto, IntroducerStatusEnum } from '@sparling/financial-common'
import { toast } from 'react-toastify'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { KeyValue } from '../../../../components/Dashboard/KeyValue'
import { SingleLabel } from '../../../../components/Dashboard/SingleLabel'
import { InputSelect } from '../../../../components/Dashboard/InputSelect'
import { MultilineLabel } from '../../../../components/Dashboard/MultilineLabel'
import { DASHBOARD_USER_ROLES } from '../../../../constants/userRoles'
import { USER_PERMISSIONS, userPermissionEnum, getReadableUserRole } from '../../../../constants/userPermissions'
import { Loader } from '../../../../components/Loader'
import { icons } from '../../../../constants/icons'
import { ReactComponent as Done } from '../../../../images/Dashboard/done_black.svg'
import capitalizeFirstLetter from '../../../../utils/capitalizeFirstLetter'
import { useRest } from '../../../../components/Context'
import { routes } from '../../../../constants/routes'
import { requestIntroducerCanUpdateUserDetails } from '../../../../store/actions'
import { requestIntroducerCanUpdateUserDetailsSelector } from '../../../../store/selectors'

const useStyles = makeStyles((theme: Theme) => ({
  formWrap: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
  },
  container: {
    minHeight: 40,
    maxHeight: 40,
    paddingBottom: theme.spacing(2),
  },
  loaderContainer: {
    background: theme.palette.background.paper,
  },
  form: {
    width: '100%',
  },
  loaderStyle: {
    display: 'flex',
    minWidth: 663,
    minHeight: 350,
    verticalAlign: 'middle',
  },
  sectionWrap: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(4, 4, 2, 4),
    background: theme.palette.background.paper,
  },
  edit: {
    width: 215,
  },
  select: {
    width: 215,
    alignItems: 'center',
  },
  editIconGray: {
    display: 'inline-block',
    color: '#a8a9ac',
    padding: 0,
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
  iconDone: {
    cursor: 'pointer',
    fill: '#A9AAAD',
    '&:hover': {
      fill: '#FFFFFF',
    },
  },
  applyButton: { display: 'flex', justifyContent: 'flex-end' },
}))

export interface GeneralSectionProps {
  title?: string
  loading?: boolean
  idSf?: string
  userAccount: GetUserProfileResponseDto | null
  refresh?: () => void
}

export const General: FC<GeneralSectionProps> = ({ title, loading = true, userAccount, idSf, refresh }) => {
  const formMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
  })
  const classes = useStyles()
  const { rxiosAuth } = useRest()
  const { watch } = formMethods
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const canUpdate = useSelector(requestIntroducerCanUpdateUserDetailsSelector)
  const [isLoading, setIsLoading] = useState<boolean>(loading)
  const [accountType, setAccountType] = useState<string>('')
  const [userRole, setUserRole] = useState<string>('')
  const [clientType, setClientType] = useState<string>('')
  const [userPermissions, setUserPermissions] = useState<string[]>([])
  const [openSelect, setOpenSelect] = useState<boolean>(false)

  const onSubmit = useCallback(
    // eslint-disable-next-line
    async (data: any) => {
      const pushData = data
      pushData.idSf = idSf

      rxiosAuth.post('user/change-user-role', pushData).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          toast.success(next.message)
          formMethods.reset({ role: pushData.role })
          setOpenSelect(false)
          if (refresh) {
            dispatch(requestIntroducerCanUpdateUserDetails(false))
            refresh()
          }
        },
        (error) => {
          toast.error(error.response.data.message)
        },
      )
    },
    // eslint-disable-next-line
    [rxiosAuth],
  )

  useEffect(() => {
    if (canUpdate && refresh) {
      dispatch(requestIntroducerCanUpdateUserDetails(false))
      refresh()
    }
  }, [dispatch, canUpdate, refresh])

  useEffect(() => {
    if (userAccount) {
      const userAccountRole = getReadableUserRole(userAccount.role)
      const userTypeRole = userAccount.role
      if (userTypeRole === 'sole_trader') {
        setAccountType('Individual')
      } else {
        setAccountType('Corporate')
      }
      setUserRole(userAccountRole)
      setClientType(userAccountRole)
    }
    setIsLoading(loading)
  }, [userAccount, isLoading, loading])

  const toggleEdit = useCallback(() => {
    setOpenSelect((prevState) => !prevState)
  }, [])

  useEffect(() => {
    const subscription = watch(async (value) => {
      setClientType(value.role)
    })
    const selectedUserType = USER_PERMISSIONS?.[clientType] || []
    setUserPermissions(selectedUserType)
    return () => subscription.unsubscribe()
  }, [watch, clientType])

  return (
    <>
      <Grid item xs={12} className={classes.formWrap}>
        <Grid item xs={12} className={classes.container}>
          <Typography variant={'h6'}>{title}</Typography>
        </Grid>

        {isLoading ? (
          <Grid item xs={12} className={classes.loaderContainer}>
            <Box className={classes.loaderStyle}>
              <Loader />
            </Box>
          </Grid>
        ) : (
          <Grid item xs={12} className={classes.sectionWrap}>
            <Grid container>
              {userAccount?.introducerStatus === IntroducerStatusEnum.REJECTED &&
                !!userAccount?.introducerRejectionReason && (
                  <Grid item xs={7}>
                    <KeyValue labelKeyValue="Rejection Reason">
                      <SingleLabel labelValue={userAccount?.introducerRejectionReason} />
                    </KeyValue>
                  </Grid>
                )}
              <Grid item xs={7}>
                <KeyValue labelKeyValue="Account Type">
                  <SingleLabel labelValue={accountType} />
                </KeyValue>
              </Grid>
            </Grid>

            <KeyValue labelKeyValue="User Type">
              {pathname === routes.ACCOUNT_ADMIN ||
              (pathname === routes.ACCOUNT_USER && userRole !== userPermissionEnum.admin) ? (
                <SingleLabel labelValue={capitalizeFirstLetter(userRole)} />
              ) : (
                <>
                  {openSelect ? (
                    <Grid container item xs={12} className={classes.select}>
                      <Grid item xs={10}>
                        <FormProvider {...formMethods}>
                          <InputSelect
                            inputName={'role'}
                            width={160}
                            enums={DASHBOARD_USER_ROLES}
                            defaultValue={clientType}
                          />
                        </FormProvider>
                      </Grid>
                      <Grid item xs={1}>
                        <SvgIcon
                          component={Done}
                          onClick={formMethods.handleSubmit(onSubmit)}
                          className={classes.iconDone}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container item xs={12} className={classes.edit}>
                      <Grid item xs={6}>
                        <SingleLabel labelValue={capitalizeFirstLetter(clientType)} />
                      </Grid>

                      <Grid item xs={1}>
                        <IconButton className={classes.editIconGray} onClick={toggleEdit}>
                          <span className="material-icons">{icons.EDIT}</span>
                        </IconButton>
                      </Grid>
                    </Grid>
                  )}
                </>
              )}
            </KeyValue>

            <KeyValue labelKeyValue="Permission" minWidth="450px">
              <MultilineLabel dataValues={userPermissions} width={320} />
            </KeyValue>
          </Grid>
        )}
      </Grid>
    </>
  )
}
